import {
  Call,
  createGeotab,
  Geotab,
  GeotabError,
  GeotabOptions,
  isOverLimitError,
  Middleware,
} from "@fairfleet/geotab";

import { assertGeotabCallShape, GeotabCallShapeError } from "./assertGeotabCallShape";

/**
 * Middleware that runs {@link assertGeotabCallShape} on every call before it is queued.
 *
 * It sits outside the call queue, so a rejected call fails alone instead of failing the whole
 * `ExecuteMultiCall` it would have been batched into. A call the server would fail is always
 * rejected. A call whose problem the server silently ignores is rejected when `strict`; otherwise
 * it is logged once per distinct problem (some calls poll) and sent, so production keeps working.
 */
export function createCallShapeGuard(strict: boolean): Middleware {
  const logged = new Set<string>();
  return next => call => {
    try {
      assertGeotabCallShape(call.method, call.params);
    } catch (error) {
      if (strict || !(error instanceof GeotabCallShapeError) || error.rejectedByServer) {
        return Promise.reject(error);
      }
      if (!logged.has(error.message)) {
        logged.add(error.message);
        console.error(error);
      }
    }
    return next(call);
  };
}

/**
 * Whether a call goes through the batch queue. Without `queueMethods` the client queues only its
 * read calls, all named `Get…`; a call that writes must never be sent twice.
 */
const isQueued = (method: string, queueMethods?: readonly string[]) =>
  queueMethods ? queueMethods.includes(method) : method.startsWith("Get");

/**
 * Calls per minute for the calls sent on their own. They are few — only the calls of a failed batch
 * and calls known to fail — and this budget comes on top of the main client's, so it stays small.
 */
const DIRECT_RATE_LIMIT = { maxCalls: 60, windowMs: 60_000 };

const callKey = (call: Call) => `${call.method}:${String(call.params?.typeName ?? "")}`;

/**
 * Middleware that keeps one failing call from failing the other calls of its `ExecuteMultiCall`.
 *
 * Geotab answers a batch with a single error as soon as one of its calls fails — a `Get AddInData`
 * the user has no clearance for took the vehicle names and status of the map down with it. A
 * queued call that fails with a Geotab error is therefore sent once more on its own through
 * `direct`, so only the call that really fails ends up failing. A call that failed on its own
 * again skips the queue from then on (by method and type name), so it no longer breaks batches
 * every time it is polled or retried.
 *
 * Over-limit errors and aborted calls are not retried: a retry would only spend more quota.
 */
export function createBatchFailureIsolation(
  direct: () => Geotab,
  queueMethods?: readonly string[],
): Middleware {
  const failsAlone = new Set<string>();
  const sendDirect = (call: Call) => direct().call(call.method, call.params, call.signal);

  return next => async call => {
    if (!isQueued(call.method, queueMethods)) {
      return next(call);
    }
    const key = callKey(call);
    if (failsAlone.has(key)) {
      return sendDirect(call);
    }
    try {
      return await next(call);
    } catch (error) {
      if (!(error instanceof GeotabError) || isOverLimitError(error) || call.signal?.aborted) {
        throw error;
      }
      try {
        return await sendDirect(call);
      } catch (directError) {
        if (directError instanceof GeotabError && !isOverLimitError(directError)) {
          failsAlone.add(key);
        }
        throw directError;
      }
    }
  };
}

/**
 * `createGeotab` with the call-shape guard (strict in dev and tests, logging in production) and
 * {@link createBatchFailureIsolation} installed.
 */
export function createGeotabClient(options: GeotabOptions = {}): Geotab {
  let directClient: Geotab | undefined;
  const direct = () =>
    (directClient ??= createGeotab({
      ...options,
      queueMethods: [],
      // Nothing is queued, but the client still refuses a queue wider than its call budget.
      queueMaxSize: 1,
      rateLimit: options.rateLimit === false ? false : DIRECT_RATE_LIMIT,
    }));

  return createGeotab({
    ...options,
    middleware: [
      ...(options.middleware ?? []),
      createCallShapeGuard(import.meta.env.DEV),
      createBatchFailureIsolation(direct, options.queueMethods),
    ],
  });
}
